import { PAUSED_UPLOAD_TTL_HOURS } from "@/components/UploadProgress/UploadProgressLoadingCard";
import { DemoUploadCard } from "../demo/DemoUploadCard";
import { ArticleBody, ArticleCallout, ArticleDemoFrame } from "./Article.elements";

export const UploadBasicsArticle = () => (
  <ArticleBody>
    <h3>Built for bad internet</h3>
    <p>
      Newsroom footage gets uploaded from car parks, press rooms and hotel Wi-Fi. The uploader was built
      for exactly that: every file is cut into small <strong>chunks</strong> and each chunk is confirmed by
      the server before the next one goes. If the connection drops, only the chunk in flight is lost —
      everything already confirmed stays on the server.
    </p>

    <h3>What you do</h3>
    <ol>
      <li>
        Click <strong>Upload clips</strong>, drop your files or pick a folder, fill in the package details and press{" "}
        <strong>Start</strong>.
      </li>
      <li>Leave the tab open. That&apos;s it.</li>
    </ol>

    <h3>What you see</h3>
    <ArticleDemoFrame>
      <DemoUploadCard state="uploading" />
    </ArticleDemoFrame>
    <ul>
      <li>
        <strong>The bar and the speed row</strong> — the only signals you need. A slow bar means a slow connection,
        not a stuck upload.
      </li>
      <li>
        <strong>Uploading files (n/total)</strong> — files upload a few at a time; the rest wait as{" "}
        <code>Queued</code>. That is normal.
      </li>
      <li>
        <strong>The icon on the left</strong> — it is a button. Pause with it, resume with it.
      </li>
    </ul>

    <h3>When the network misbehaves</h3>
    <p>
      The card tells you what is happening (<em>Reconnecting…</em>, <em>Connection stalled — retrying
      automatically…</em>, <em>You appear to be offline</em>) and keeps trying on its own. The moment the network is
      back it continues from the last confirmed chunk. If the connection stays bad for a long time the upload is{" "}
      <strong>paused, not failed</strong>; it resumes itself when it can, or with one press of the play icon.
    </p>

    <ArticleCallout>
      A paused upload is kept for <strong>{PAUSED_UPLOAD_TTL_HOURS} hours</strong>. Move to better Wi-Fi, come
      back, press play — nothing already uploaded is sent again.
    </ArticleCallout>

    <h3>Closed the tab? Reloaded the page?</h3>
    <p>
      Open the app again. The card is back under <strong>Uploading</strong>. Because browsers don&apos;t let a page
      keep your files after a reload, you select the same files (or folder) once more — the uploader skips every
      chunk that already arrived.
    </p>

    <h3>One upload at a time</h3>
    <p>
      An account uploads one package at a time. If you start on a second computer while the first is still going,
      you&apos;ll see the package marked <em>another device</em> with a <strong>Take over here</strong> button — use it
      instead of cancelling.
    </p>
  </ArticleBody>
);
